import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add session token from localStorage as fallback for mobile browsers that block cookies
api.interceptors.request.use((config) => {
  const sessionToken = localStorage.getItem('session_token');
  if (sessionToken) {
    config.headers.Authorization = `Bearer ${sessionToken}`;
  }
  return config;
});

// Store session token in localStorage when received (for mobile compatibility)
api.interceptors.response.use((response) => {
  if (response.data?.session_token) {
    localStorage.setItem('session_token', response.data.session_token);
  }
  return response;
});

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  processSession: (sessionId) => api.post('/auth/session', { session_id: sessionId }),
  getMe: () => api.get('/auth/me'),
  logout: async () => {
    localStorage.removeItem('session_token');
    return api.post('/auth/logout');
  },
};

// Leads APIs
export const leadsAPI = {
  getAll: () => api.get('/leads'),
  getOne: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
};

// Lenders APIs
export const lendersAPI = {
  getAll: (category) => api.get('/lenders', { params: { category } }),
  getOne: (id) => api.get(`/lenders/${id}`),
  create: (data) => api.post('/lenders', data),
  update: (id, data) => api.put(`/lenders/${id}`, data),
  delete: (id) => api.delete(`/lenders/${id}`),
  uploadProgram: (id, formData) => api.post(`/lenders/${id}/upload-program`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  parseProgram: (lenderId, docId) => api.post(`/lenders/${lenderId}/parse-program/${docId}`),
  deleteProgram: (lenderId, docId) => api.delete(`/lenders/${lenderId}/programs/${docId}`),
};

// Applications APIs
export const applicationsAPI = {
  getAll: () => api.get('/applications'),
  getOne: (id) => api.get(`/applications/${id}`),
  create: (data) => api.post('/applications', data),
  update: (id, data) => api.put(`/applications/${id}`, data),
  updateStatus: (id, status) => api.put(`/applications/${id}/status`, { status }),
};

// Communications APIs
export const communicationsAPI = {
  sendEmail: (data) => api.post('/communications/email', data),
  sendSMS: (data) => api.post('/communications/sms', data),
  getLogs: (leadId) => api.get('/communications/logs', { params: { lead_id: leadId } }),
};

// Follow-ups APIs
export const followupsAPI = {
  getAll: () => api.get('/followups'),
  create: (data) => api.post('/followups', data),
  complete: (id) => api.put(`/followups/${id}/complete`),
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Admin APIs
export const adminAPI = {
  getUsers: () => api.get('/admin/users'),
};
